import { useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Shared checkout hook — enforces sign-up/sign-in before any Stripe checkout.
 * Two usage patterns:
 *   buy(productId, label)                   → single-item product checkout
 *   buy(null, label, { items, kind })       → multi-item (cart) or membership
 */
export function useCheckout() {
  const [loadingId, setLoadingId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();

  const buy = useCallback(
    async (productId, label, opts = {}) => {
      const kind = opts.kind || "product";
      const items = opts.items || (productId ? [{ product_id: productId, quantity: 1 }] : []);
      const key = productId || opts.loadingKey || "checkout";

      if (authLoading) return;
      if (!user) {
        toast.info("Create a free account or sign in to check out.");
        navigate(`/register?redirect=${encodeURIComponent(location.pathname + location.search)}`);
        return;
      }

      setLoadingId(key);
      try {
        const payload = { kind, origin_url: window.location.origin };
        if (kind === "product") payload.items = items;
        const res = await api.post("/checkout/session", payload);
        window.location.href = res.data.url;
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401) {
          toast.info("Please sign in to check out.");
          navigate(`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`);
        } else {
          console.error(err);
          toast.error(`Couldn't open checkout${label ? ` for ${label}` : ""}. Please try again.`);
        }
        setLoadingId(null);
      }
    },
    [authLoading, user, navigate, location]
  );

  return { buy, loadingId };
}
